// Chakra Imports
import {
  Avatar,
  Box,
  Flex,
  Image,
  Icon,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useColorModeValue,
  useToast,
  HStack,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { useState, useEffect, useCallback } from 'react';
import React from 'react';
import {
  MdNotificationsNone,
  MdOutlinePerson,
} from 'react-icons/md';
import { FaFilePdf } from 'react-icons/fa';
import axiosInstance from 'axiosInstance';
import baseUrl from 'Base_Url/baseUrl';
import { notifyApiErrorAsync } from 'utils/apiFeedback';

import { useUser } from '../../userContext';

// ---------------------
// ICON COMPONENTS
// ---------------------

const LogOutIcon = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
    <polyline points="16 17 21 12 16 7"></polyline>
    <line x1="21" x2="9" y1="12" y2="12"></line>
  </svg>
);

// ---------------------
// DOCUMENT DETAILS SECTION
// ---------------------

const DocumentDetails = ({ data, onDownloadPdf, isDownloadingPdf }) => {
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const highlightColor = useColorModeValue('blue.600', 'aqua.300');

  return (
    <Flex align="center" gap={6} color={textColor} fontSize="sm">
      <Box textAlign="right">
        <Text fontWeight="semibold" fontSize="md">
          NOC No:{' '}
          <Text as="span" color={highlightColor}>
            {data.nocNo}
          </Text>
        </Text>
        <Text whiteSpace="nowrap" fontWeight="medium">
          Permitted Quantity:{' '}
          <Text as="span" color={highlightColor} fontWeight="semibold">
            {data.permittedQty}
          </Text>
        </Text>
        <Text fontSize="sm" fontWeight="medium">
          Valid From -{' '}
          <Text as="span" fontWeight="semibold" color={highlightColor}>
            {data.validFrom}
          </Text>{' '}
          | Valid Up To{' '}
          <Text as="span" fontWeight="semibold" color={highlightColor}>
            {data.validUpTo}
          </Text>
        </Text>
      </Box>

      {/* PDF Button */}
      <Flex
        direction="column"
        align="center"
        justify="center"
        bg="red.500"
        p={3}
        borderRadius="lg"
        boxShadow="md"
        cursor={isDownloadingPdf ? 'not-allowed' : 'pointer'}
        opacity={isDownloadingPdf ? 0.7 : 1}
        onClick={isDownloadingPdf ? undefined : onDownloadPdf}
        _hover={{ bg: 'red.600' }}
      >
        <Icon as={FaFilePdf} w={8} h={4} color="white" />
        <Text color="white" fontSize="sm" fontWeight="bold" mt={1}>
          PDF
        </Text>
      </Flex>
    </Flex>
  );
};

// ---------------------
// MAIN COMPONENT
// ---------------------

export default function HeaderLinks() {
  const toast = useToast();
  const [companyName, setCompanyName] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDownloadingNoc, setIsDownloadingNoc] = useState(false);

  const Username = localStorage.getItem('name');
  const userEmail = localStorage.getItem('email');
  const role = localStorage.getItem('role');
  const nocNo = localStorage.getItem('nocNumber');
  const nocStartDate = localStorage.getItem('nocStartDate');
  const nocEndDate = localStorage.getItem('nocEndDate');
  const limitPerDay = localStorage.getItem('limitPerDay');
  const limitPerYear = localStorage.getItem('limitPerYear');
  const Token = localStorage.getItem('token');

  const { licenseDatas, updateLicenseData } = useUser();
  const borewellCount =
    licenseDatas?.borewellCount ?? localStorage.getItem('borewellCount') ?? 0;
  const companyLogoUrl =
    licenseDatas?.companyLogoUrl || localStorage.getItem('companyLogoUrl') || '';

  const userData = {
    name: Username,
    role: role === 'companyUser' ? 'Admin' : role,
    avatarUrl: 'https://placehold.co/100x100/1e40af/ffffff?text=RK',
  };

  const convertDates = (input) => {
    const date = new Date(input);
    const formattedDate = date.toLocaleDateString('en-GB');
    return formattedDate;
  };

  const getFileNameFromDisposition = useCallback((disposition) => {
    const utf8FileName = disposition?.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8FileName?.[1]) {
      return decodeURIComponent(utf8FileName[1]);
    }

    const fileName = disposition?.match(/filename="?([^";]+)"?/i);
    if (fileName?.[1]) {
      return fileName[1];
    }

    return localStorage.getItem('nocPdfFileName') || 'NOC.pdf';
  }, []);

  const handleNocPdfDownload = useCallback(async () => {
    const companyId = localStorage.getItem('companyId');

    if (
      !Token ||
      !companyId ||
      companyId === 'undefined' ||
      companyId === 'null'
    ) {
      toast({
        title: 'Company NOC is not available',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsDownloadingNoc(true);
      const response = await axiosInstance.get(
        `${baseUrl}/company/noc/${companyId}/download`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
          responseType: 'blob',
          skipGlobalErrorToast: true,
        },
      );

      const blob = new Blob([response.data], {
        type: response.headers?.['content-type'] || 'application/pdf',
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = getFileNameFromDisposition(
        response.headers?.['content-disposition'],
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      await notifyApiErrorAsync(error, toast, 'Unable to download NOC PDF');
    } finally {
      setIsDownloadingNoc(false);
    }
  }, [Token, getFileNameFromDisposition, toast]);

  const fetchBorewellCount = useCallback(async () => {
    if (!Token || !role) return;

    try {
      let url = `${baseUrl}/device-readings/fetchOverviewData`;

      if (role === 'admin') {
        const companyId = localStorage.getItem('companyId');
        if (!companyId) return;
        url = `${url}?companyId=${companyId}`;
      } else if (!['companyUser', 'user'].includes(role)) {
        return;
      }

      const response = await axiosInstance.get(url, {
        headers: {
          Authorization: `Bearer ${Token}`,
        },
      });
      const data = response?.data?.data?.data;
      updateLicenseData({
        borewellCount: Array.isArray(data) ? data.length : 0,
      });
    } catch (error) {
      console.error('Failed to fetch borewell count', error);
    }
  }, [Token, role, updateLicenseData]);

  const licenseData = {
    nocNo: nocNo,
    permittedQty: `${limitPerDay}m³/Day | ${limitPerYear}m³/Year, BW - ${borewellCount}Nos`,
    validFrom: convertDates(nocStartDate),
    validUpTo: convertDates(nocEndDate),
  };

  useEffect(() => {
    const cName = localStorage.getItem('companyName');
    if (cName) setCompanyName(cName);
  }, []);

  useEffect(() => {
    fetchBorewellCount();
  }, [fetchBorewellCount]);

  const handleLogout = () => {
    setTimeout(() => {
      localStorage.removeItem('token');
      localStorage.removeItem('district');
      localStorage.removeItem('name');
      localStorage.removeItem('email');
      localStorage.removeItem('companyName');
      localStorage.removeItem('username');
      localStorage.removeItem('role');
      localStorage.removeItem('nocNumber');
      localStorage.removeItem('nocStartDate');
      localStorage.removeItem('nocEndDate');
      localStorage.removeItem('limitPerDay');
      localStorage.removeItem('limitPerYear');
      localStorage.removeItem('nocPdfFileName');
      localStorage.removeItem('borewellCount');
      localStorage.removeItem('companyLogoUrl');

      window.location.href = '/auth/sign-in';
      toast({
        title: 'Logged out of your account!',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    }, 500);
    setIsMenuOpen(false);
  };

  const handleNotificationClick = useCallback(() => {
  }, []);

  const handleProfileClick = () => {
    setIsMenuOpen(false);
    window.location.href = '/admin/profile';
  };

  // Chakra colors
  const navBg = useColorModeValue('white', 'dark.850');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const borderColor = useColorModeValue('gray.200', 'dark.600');
  const menuBg = useColorModeValue('white', 'dark.800');
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400');
  const menuHoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const menuIconColor = useColorModeValue('gray.500', 'gray.400');
  const activeModeColor = useColorModeValue(
    'rgba(0, 106, 184, 1)',
    'aqua.300',
  );
  const topIconBg = useColorModeValue('gray.200', 'dark.700');
  const topIconHoverBg = useColorModeValue('gray.300', 'dark.600');
  const topIconColor = useColorModeValue('gray.600', 'gray.200');
  const avatarBg = useColorModeValue('gray.200', 'dark.700');
  const avatarBorderColor = useColorModeValue('white', 'dark.600');

  return (
    <Flex
      as="nav"
      position="fixed"
      top="0"
      left={{ base: 4, xl: 290 }}
      w={{ base: '100%', xl: 'calc(100% - 290px)' }}
      bg={navBg}
      borderBottom="1px solid"
      borderColor={borderColor}
      zIndex="-1 !important"
      align="center"
      justify="space-between"
      px={{ base: 8, md: 8 }}
      h="20"
    >
      {/* --- Left: Logo --- */}
      <Flex align="center" minW={{ base: '110px', md: '180px' }}>
        {companyLogoUrl && (
          <Image
            src={companyLogoUrl}
            alt={companyName ? `${companyName} logo` : 'Company logo'}
            h={{ base: '42px', md: '54px' }}
            maxW={{ base: '110px', md: '180px' }}
            objectFit="contain"
          />
        )}
        {/* <Text fontSize="3xl" fontWeight="extrabold">
          <Text
            as="span"
            bgGradient="linear(to-r, purple.400, blue.600)"
            bgClip="text"
          >
            g
          </Text>
          <Text as="span" color={textColor} ml={1}>
            iven.
          </Text>
        </Text> */}
      </Flex>

      {/* --- Center: Document Details --- */}
      <Box
        display={{ base: 'none', lg: 'flex' }}
        // flex="1"
        justifyContent="center"
      >
        <DocumentDetails
          data={licenseData}
          onDownloadPdf={handleNocPdfDownload}
          isDownloadingPdf={isDownloadingNoc}
        />
      </Box>

      {/* --- Right: Icons & Profile --- */}
      <HStack spacing={4}>
        {/* Notification Button */}
        <Box position="relative">
          <Flex
            w="12"
            h="12"
            align="center"
            justify="center"
            borderRadius="full"
            boxShadow="sm"
            cursor="pointer"
            bg={topIconBg}
            _hover={{ bg: topIconHoverBg }}
            onClick={handleNotificationClick}
          >
            <Box
              position="absolute"
              top="3"
              right="3"
              w="2"
              h="2"
              bg={activeModeColor}
              borderRadius="full"
            />
            <Icon as={MdNotificationsNone} w={5} h={5} color={topIconColor} />
          </Flex>
        </Box>

        {/* Profile Menu */}
        <Menu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)}>
          <MenuButton onClick={() => setIsMenuOpen(!isMenuOpen)}>
            <HStack spacing={3}>
              <Avatar
                size="md"
                name={userData.name}
                // src={userData.avatarUrl}
                border="2px solid"
                borderColor={avatarBorderColor}
                boxShadow="sm"
                bg={avatarBg}
              />
              <Box
                display={{ base: 'none', sm: 'flex' }}
                flexDir="column"
                textAlign="left"
              >
                <Text fontWeight="semibold" fontSize="sm" color={textColor}>
                  {userData.name}
                </Text>
                <Text fontSize="xs" color={mutedTextColor} mt="-5px">
                  {userData.role}
                </Text>
              </Box>
              <Icon
                viewBox="0 0 24 24"
                color={mutedTextColor}
                transform={isMenuOpen ? 'rotate(180deg)' : ''}
                transition="0.2s"
              >
                <polyline
                  points="6 9 12 15 18 9"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </Icon>
            </HStack>
          </MenuButton>

          <MenuList
            bg={menuBg}
            border="1px solid"
            borderColor={borderColor}
            shadow="0 12px 32px rgba(15, 23, 42, 0.14)"
            borderRadius="8px"
            p={4}
            minW="290px"
          >
            <Box px={1} pb={3}>
              <Text fontSize="md" fontWeight="700" color={textColor}>
                {userData.name || 'User'}
              </Text>
              <Text
                fontSize="sm"
                color={mutedTextColor}
                noOfLines={1}
                mt="-1px"
              >
                {userEmail || 'No email available'}
              </Text>
            </Box>

            <Box h="1px" bg={borderColor} mb={2} />

            <MenuItem
              icon={
                <Icon
                  as={MdOutlinePerson}
                  boxSize={5}
                  color={menuIconColor}
                />
              }
              onClick={handleProfileClick}
              color={textColor}
              borderRadius="6px"
              fontWeight="600"
              py={3}
              _hover={{ bg: menuHoverBg }}
              _focus={{ bg: menuHoverBg }}
            >
              My Profile
            </MenuItem>
            <MenuItem
              icon={<LogOutIcon />}
              onClick={handleLogout}
              color="red.600"
              borderRadius="6px"
              fontWeight="600"
              py={3}
              _hover={{ bg: 'red.50', color: 'red.700' }}
              _focus={{ bg: 'red.50', color: 'red.700' }}
            >
              Logout
            </MenuItem>
          </MenuList>
        </Menu>
      </HStack>
    </Flex>
  );
}

// PropTypes
HeaderLinks.propTypes = {
  variant: PropTypes.string,
  fixed: PropTypes.bool,
  secondary: PropTypes.bool,
  onOpen: PropTypes.func,
};
